import BigNumber from 'bignumber.js';

export interface StockFeeResult {
    broker: BigNumber;
    exchange: BigNumber;
    clearing: BigNumber;
    sec: BigNumber;
    finra: BigNumber;
    finraCat: BigNumber;
    total: BigNumber;
    entrySideTotal: BigNumber;
    exitSideTotal: BigNumber;
}

export interface StockFeeParams {
    brokerMode: string;        // 'perShare' | 'fixed'
    brokerRate: BigNumber;
    brokerMin: BigNumber;
    brokerMaxPct: BigNumber;
    exchangeRate: BigNumber;
    clearingRate: BigNumber;
    clearingMaxPct: BigNumber;
    secRate: BigNumber;        // raw rate (already ÷ 1M)
    finraRate: BigNumber;      // raw rate (already ÷ 1K)
    finraMax: BigNumber;
    finraCatRate: BigNumber;   // raw rate (already ÷ 1K)
}

function computeBrokerPerSide(shares: BigNumber, tradeValue: BigNumber, params: StockFeeParams): BigNumber {
    if (params.brokerMode === 'fixed') {
        return params.brokerRate;
    }
    // perShare mode
    const raw = shares.times(params.brokerRate);
    const maxCap = tradeValue.times(params.brokerMaxPct).dividedBy(100);
    const capped = BigNumber.min(raw, maxCap);
    return BigNumber.max(capped, params.brokerMin);
}

function computeClearingPerSide(shares: BigNumber, tradeValue: BigNumber, params: StockFeeParams): BigNumber {
    const raw = shares.times(params.clearingRate);
    const maxCap = tradeValue.times(params.clearingMaxPct).dividedBy(100);
    return BigNumber.min(raw, maxCap);
}

export function computeStockFees(
    shares: BigNumber,
    entryPrice: BigNumber,
    exitPrice: BigNumber,
    params: StockFeeParams,
    isLong: boolean,
): StockFeeResult {
    const tradeValueEntry = shares.times(entryPrice);
    const tradeValueExit = shares.times(exitPrice);

    // Broker (both sides)
    const brokerEntry = computeBrokerPerSide(shares, tradeValueEntry, params);
    const brokerExit = computeBrokerPerSide(shares, tradeValueExit, params);
    const broker = brokerEntry.plus(brokerExit);

    // Exchange/ECN (per share, both sides)
    const exchangeOneSide = shares.times(params.exchangeRate);
    const exchange = exchangeOneSide.plus(exchangeOneSide);

    // Clearing (per share with max cap, both sides)
    const clearingEntry = computeClearingPerSide(shares, tradeValueEntry, params);
    const clearingExit = computeClearingPerSide(shares, tradeValueExit, params);
    const clearing = clearingEntry.plus(clearingExit);

    // SEC (sell side only, rate × sell value)
    const sellValue = isLong ? tradeValueExit : tradeValueEntry;
    const sec = sellValue.times(params.secRate);

    // FINRA TAF (sell side only, per share with max cap)
    const finraRaw = shares.times(params.finraRate);
    const finra = BigNumber.min(finraRaw, params.finraMax);

    // FINRA CAT (sell side only, per share, no cap)
    const finraCat = shares.times(params.finraCatRate);

    // Total
    const total = broker.plus(exchange).plus(clearing).plus(sec).plus(finra).plus(finraCat);

    // Entry/exit side split
    let entrySideTotal = brokerEntry.plus(exchangeOneSide).plus(clearingEntry);
    let exitSideTotal = brokerExit.plus(exchangeOneSide).plus(clearingExit);

    // Add SEC, FINRA TAF and FINRA CAT to sell side
    if (isLong) {
        exitSideTotal = exitSideTotal.plus(sec).plus(finra).plus(finraCat);
    } else {
        entrySideTotal = entrySideTotal.plus(sec).plus(finra).plus(finraCat);
    }

    return { broker, exchange, clearing, sec, finra, finraCat, total, entrySideTotal, exitSideTotal };
}

export function formatStockFeeBreakdown(feeResult: StockFeeResult): Record<string, string> {
    const fmt = (v: BigNumber) => v.toFixed(2);
    return {
        broker: fmt(feeResult.broker),
        exchange: fmt(feeResult.exchange),
        clearing: fmt(feeResult.clearing),
        sec: fmt(feeResult.sec),
        finra: fmt(feeResult.finra),
        finraCat: fmt(feeResult.finraCat),
        total: fmt(feeResult.total),
    };
}
